import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { houseRules } from '../mock';
import { Clock, AlertTriangle, CheckCircle, FileText } from 'lucide-react';

const HouseRules = () => {
  return (
    <div className="bg-slate-50 min-h-screen py-12">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">House Rules & Policies</h1>
          <p className="text-lg text-slate-600">
            Please review our house rules and policies to ensure a pleasant stay for everyone.
          </p>
        </div>

        {/* Check-in/Check-out Times */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Clock className="h-5 w-5 mr-2" />
              Check-in & Check-out Times
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-50 rounded-lg p-6">
                <h3 className="font-semibold text-slate-900 mb-2">Check-in Time</h3>
                <p className="text-2xl font-bold text-slate-800">{houseRules.checkIn}</p>
                <p className="text-sm text-slate-600 mt-2">
                  Early check-in may be available upon request and subject to availability.
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg p-6">
                <h3 className="font-semibold text-slate-900 mb-2">Check-out Time</h3>
                <p className="text-2xl font-bold text-slate-800">{houseRules.checkOut}</p>
                <p className="text-sm text-slate-600 mt-2">
                  Late check-out may be available upon request and subject to availability.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* House Rules */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2" />
              House Rules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {houseRules.rules.map((rule, idx) => (
                <div key={idx} className="border-b border-slate-200 last:border-0 pb-6 last:pb-0">
                  <h3 className="font-bold text-lg text-slate-900 mb-2">{rule.title}</h3>
                  <p className="text-slate-600">{rule.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Cancellation Policy */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Cancellation Policy</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600 mb-4">{houseRules.cancellationPolicy}</p>
            <Alert>
              <AlertDescription>
                Cancellations must be made through email or phone. We recommend purchasing travel insurance 
                for added protection against unforeseen circumstances.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Additional Policies */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Additional Policies</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Property Access</h3>
                <p className="text-slate-600">
                  You will receive detailed check-in instructions via email 24 hours before your arrival. 
                  Most properties feature keyless entry systems for your convenience.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Guest Identification</h3>
                <p className="text-slate-600">
                  All guests are required to provide valid government-issued identification before check-in 
                  for security purposes.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Parking</h3>
                <p className="text-slate-600">
                  Parking availability varies by property. Properties with dedicated parking will have 
                  details included in the listing. Street parking is available at most locations.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Maintenance & Support</h3>
                <p className="text-slate-600">
                  Our team is available 24/7 to address any maintenance issues or questions during your stay. 
                  Contact information will be provided in your check-in instructions.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Property Damages</h3>
                <p className="text-slate-600">
                  Guests are responsible for any damages beyond normal wear and tear. Please report any 
                  pre-existing damage upon check-in. A damage deposit may be required for certain properties.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Good Guest Guidelines */}
        <Card className="bg-slate-800 text-white border-slate-700">
          <CardHeader>
            <CardTitle className="flex items-center text-white">
              <CheckCircle className="h-5 w-5 mr-2" />
              Being a Great Guest
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-300 mb-4">
              We appreciate guests who treat our properties with care and respect. Here are a few tips:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300">
              <li>Keep noise levels down, especially during quiet hours (10 PM - 8 AM)</li>
              <li>Take care of the property as if it were your own home</li>
              <li>Communicate with us promptly if any issues arise</li>
              <li>Follow recycling and trash guidelines</li>
              <li>Lock doors and windows when leaving the property</li>
              <li>Report any maintenance issues immediately</li>
              <li>Leave the property in the same condition as you found it</li>
            </ul>
          </CardContent>
        </Card>

        {/* Terms & Conditions */}
        {houseRules.termsAndConditions && (
          <Card className="mt-8 mb-8">
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                {houseRules.termsAndConditions.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-6">
                By confirming your booking and submitting payment, you acknowledge that you have read, 
                understood, and agreed to all terms outlined below.
              </p>
              <div className="space-y-6">
                {houseRules.termsAndConditions.sections.map((section, idx) => (
                  <div key={idx} className="border-b border-slate-200 last:border-0 pb-6 last:pb-0">
                    <h3 className="font-bold text-lg text-slate-900 mb-2">{idx + 1}. {section.title}</h3>
                    <p className="text-slate-600">{section.content}</p>
                  </div>
                ))}
              </div>
              <Alert className="mt-6 border-amber-200 bg-amber-50">
                <AlertDescription className="text-amber-800">
                  <strong>Agreement & Acknowledgment:</strong> By completing your booking, you confirm your 
                  agreement to all Terms and Conditions listed above. Failure to comply may result in 
                  termination of your reservation without refund.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}

        {/* Questions CTA */}
        <div className="mt-10 text-center bg-white rounded-lg p-8 shadow-sm">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Questions About Our Policies?</h2>
          <p className="text-slate-600 mb-6">
            If you have any questions about our house rules or policies, please don't hesitate to contact us.
          </p>
          <a href="/contact">
            <button className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-3 rounded-md font-medium transition-colors duration-200">
              Contact Us
            </button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default HouseRules;